import { useEffect } from 'react';

import {
  useSelectSubscription,
  UseSelectSubscriptionsReturn,
  scanIqSubscriptionsService,
  pathQueryParams,
  checkoutService,
} from '../../payment';
import { entitySignUpStore } from '../../shared/stores/entity/entitySignUpStore';
import { getCurrentUrl, goTo } from '../../shared/utils/angularUtils';
import { SCAN_IQ_PAYMENT_RESULT_TYPE } from '../../payment/constants/scanIqPaymentResultType';

type UseScanIqSubscriptionsReturn = UseSelectSubscriptionsReturn & {
  handleSubmit: () => void;
};

// Consumer component should be observer
export const useScanIqSubscriptions = (): UseScanIqSubscriptionsReturn => {
  const { entityId } = entitySignUpStore;

  const onApplyPromoCode = async (promoCode: string) => {
    if (!entityId) {
      throw new Error('entityId is not defined');
    }

    return scanIqSubscriptionsService.applyPromoCode(entityId, promoCode);
  };

  const selectSubscriptionInfo = useSelectSubscription({ onApplyPromoCode });

  const {
    setSubscriptions,
    setDiscountPercent,
    isFreeSubscription,
    selectedSubscription,
  } = selectSubscriptionInfo;

  const getSubscriptions = async () => {
    if (!entityId) {
      throw new Error('entityId is not defined');
    }

    const { subscriptions, promocode } =
      await scanIqSubscriptionsService.getSubscriptions(entityId);
    setSubscriptions(subscriptions);
    setDiscountPercent(promocode?.discountPercent ?? 0);
  };

  useEffect(() => {
    getSubscriptions();
  }, []);

  const currentUrl = getCurrentUrl();

  const handleSubmit = async () => {
    if (!selectedSubscription || !entityId) {
      throw new Error('Not all required data is set');
    }

    if (isFreeSubscription) {
      await checkoutService.createFreeScanIqSubscription(
        entityId,
        selectedSubscription.id,
      );

      goTo('/scaniq-payment-result', {
        params: {
          [pathQueryParams.entityId]: entityId,
          [pathQueryParams.redirectStatus]: 'succeeded',
          [pathQueryParams.retryPath]: currentUrl,
          [pathQueryParams.paymentResultType]: SCAN_IQ_PAYMENT_RESULT_TYPE.FREE,
        },
      });
    } else {
      goTo('/scan-iq-checkout', {
        params: {
          [pathQueryParams.entityId]: entityId,
          [pathQueryParams.selectedSubscriptionId]: selectedSubscription.id,
          [pathQueryParams.retryPath]: currentUrl,
          [pathQueryParams.subscriptionType]: selectedSubscription.subscriptionType,
          [pathQueryParams.paymentResultType]: SCAN_IQ_PAYMENT_RESULT_TYPE.WITH_PAYMENT,
        },
      });
    }
  };

  return { ...selectSubscriptionInfo, handleSubmit };
};
